import PropTypes from 'prop-types';
import { cloneElement, isValidElement } from 'react';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { Box, Card, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';

const getIconColor = icon => {
  if (isValidElement(icon)) {
    return icon.props.sx?.color || icon.props.color || '#0b7fad';
  }

  return '#0b7fad';
};

const hexToRgba = (hex, alpha) => {
  const normalizedHex = hex.replace('#', '');
  if (normalizedHex.length !== 6) {
    return hex;
  }

  const red = parseInt(normalizedHex.slice(0, 2), 16);
  const green = parseInt(normalizedHex.slice(2, 4), 16);
  const blue = parseInt(normalizedHex.slice(4, 6), 16);
  return `rgba(${red}, ${green}, ${blue}, ${alpha})`;
};

const renderIcon = (icon, sx, className) => {
  if (!icon) {
    return null;
  }

  if (typeof icon === 'function') {
    const IconComponent = icon;
    return <IconComponent className={className} sx={sx} />;
  }

  if (isValidElement(icon)) {
    return cloneElement(icon, {
      className: [icon.props.className, className].filter(Boolean).join(' '),
      sx: {
        ...(icon.props.sx || {}),
        ...sx,
      },
    });
  }

  return icon;
};

export const AdminCenterCard = ({ icon, title, info, navigateText, navigateTo, navigationItems = [] }) => {
  const iconColor = getIconColor(icon);
  const navigate = useNavigate();
  const links = navigationItems.length > 0 ? navigationItems : [{ text: navigateText, to: navigateTo }];

  const handleNavigate = to => {
    if (!to) {
      return;
    }

    if (/^(https?:)?\/\//.test(to)) {
      window.location.assign(to);
      return;
    }

    navigate(to);
  };

  const handleNavigateKeyDown = (event, to) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleNavigate(to);
    }
  };

  return (
    <Card
      className="admin-center-card"
      sx={{
        position: 'relative',
        overflow: 'hidden',
        width: '100%',
        height: 372,
        borderRadius: '7px',
        border: '1px solid #d6dde4',
        boxShadow: '0 1px 3px rgba(23, 43, 77, 0.12)',
        backgroundColor: '#fff',
        display: 'flex',
        flexDirection: 'column',
        p: '26px 26px 28px',
      }}>
      <Box
        className="admin-center-card__background-icon"
        sx={{
          position: 'absolute',
          top: 34,
          right: 34,
          opacity: 0.12,
          transform: 'scale(2.7)',
          transformOrigin: 'top right',
        }}>
        {renderIcon(icon, { color: iconColor, fontSize: 31 }, 'admin-center-card__background-icon-svg')}
      </Box>

      <Box
        className="admin-center-card__icon-container"
        sx={{
          width: 50,
          height: 50,
          borderRadius: '3px',
          backgroundColor: hexToRgba(iconColor, 0.12),
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: '28px',
        }}>
        {renderIcon(icon, { color: iconColor, scale: 1.5 }, 'admin-center-card__icon')}
      </Box>

      <Typography
        className="admin-center-card__title"
        sx={{
          color: '#152536',
          fontSize: 24,
          fontWeight: 700,
          lineHeight: 1.2,
          mb: '12px',
        }}>
        {title}
      </Typography>

      <Typography
        className="admin-center-card__info"
        sx={{
          color: '#6d7178',
          fontSize: 18,
          fontWeight: 600,
          lineHeight: 1.5,
          minHeight: 82,
          maxWidth: 350,
          whiteSpace: 'pre-line',
        }}>
        {info}
      </Typography>

      <Box
        sx={{
          borderTop: '1px solid #edf0f2',
          display: 'flex',
          flexDirection: 'column',
          gap: navigationItems.length > 0 ? 1.5 : 0,
          mt: 'auto',
          pt: '19px',
        }}>
        {links.map(item => (
          <Box
            className="admin-center-card__navigate-container"
            key={item.to || item.text}
            onClick={() => handleNavigate(item.to)}
            onKeyDown={event => handleNavigateKeyDown(event, item.to)}
            role={item.to ? 'button' : undefined}
            tabIndex={item.to ? 0 : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              '&:hover': {
                cursor: item.to ? 'pointer' : 'default',
              },
            }}>
            <Typography
              className="admin-center-card__navigate-text"
              sx={{
                color: iconColor,
                fontSize: 13,
                fontWeight: 800,
                letterSpacing: 1.4,
                textTransform: 'uppercase',
              }}>
              {item.text}
            </Typography>
            <ArrowForwardIcon className="admin-center-card__navigate-arrow" sx={{ color: iconColor, fontSize: 24 }} />
          </Box>
        ))}
      </Box>
    </Card>
  );
};

AdminCenterCard.propTypes = {
  icon: PropTypes.oneOfType([PropTypes.element, PropTypes.elementType]),
  title: PropTypes.node.isRequired,
  info: PropTypes.node.isRequired,
  navigateText: PropTypes.node,
  navigateTo: PropTypes.string,
  navigationItems: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.node.isRequired,
      to: PropTypes.string.isRequired,
    }),
  ),
};

AdminCenterCard.defaultProps = {
  icon: null,
  navigateText: '',
  navigateTo: '',
  navigationItems: [],
};

export default AdminCenterCard;
